import { useMemo, useRef } from 'react';
import { useProcessWithdrawal } from '../../../application/hooks/useCommissions';
import { useTranslation } from '../../../application/hooks/useTranslation';
import { MIN_WITHDRAWAL } from '../../../domain/calculators/withdrawalFee';
import { minGrossToReach, quoteProcessing } from '../../../domain/calculators/processWithdrawal';
import { paisaToTaka, toPaisa } from '../../../domain/money';
import { formatBDT } from '../../../domain/formatters/currency';
import { IdempotencyKeyHolder } from '../../../domain/withdrawals/idempotencyKey';
import { parseWithdrawalError } from '../../../domain/withdrawals/withdrawalErrors';
import { AmountDisplay } from '../ui/AmountDisplay';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';

type T = (key: string, vars?: Record<string, string | number>) => string;

/** Localised copy for a failed process-withdrawal call, chosen from the problem `code` (never the English detail). */
export function processErrorText(error: unknown, t: T): string {
  const info = parseWithdrawalError(error);
  switch (info.code) {
    case 'VALIDATION_FAILED':
      return t('processWithdrawal.errZero');
    case 'INVALID_TRANSITION':
      return t('processWithdrawal.errChanged');
    case 'IDEMPOTENCY_CONFLICT':
      return t('processWithdrawal.errConflict');
    case 'FORBIDDEN_TIER':
      return t('processWithdrawal.errTier');
    default:
      if (info.status === null) return t('processWithdrawal.errNetwork'); // outcome unknown; same-key retry is safe
      return t('processWithdrawal.errGeneric');
  }
}

export interface ProcessWithdrawalModalProps {
  open: boolean;
  onClose: () => void;
  /** The member's whole Available commission, as currently shown. */
  availableCommission: number;
  /** What the member already holds in Available for withdrawal (already net of any earlier charge). Defaults to 0. */
  availableForWithdrawal?: number;
}

/**
 * Confirmation for the one-way "Process for withdrawal" action. The body is mounted per open, so each open gets a
 * fresh Idempotency-Key and mutation state; a retry of the same attempt (same gross) reuses its key.
 */
export function ProcessWithdrawalModal({ open, onClose, availableCommission, availableForWithdrawal = 0 }: ProcessWithdrawalModalProps) {
  const { t } = useTranslation();
  return (
    <Modal open={open} title={t('processWithdrawal.title')} onClose={onClose}>
      <ProcessBody onClose={onClose} availableCommission={availableCommission} availableForWithdrawal={availableForWithdrawal} />
    </Modal>
  );
}

function ProcessBody({ onClose, availableCommission, availableForWithdrawal = 0 }: Omit<ProcessWithdrawalModalProps, 'open'>) {
  const { t, locale } = useTranslation();
  const mutation = useProcessWithdrawal();
  const keys = useRef(new IdempotencyKeyHolder());
  const inFlight = useRef(false);

  const quote = useMemo(() => quoteProcessing(availableCommission), [availableCommission]);
  const rate = quote.feeRatePercent;
  const zero = quote.grossAmount <= 0;
  const result = mutation.data;

  // Non-blocking heads-up: after processing, would Available for withdrawal still be under the minimum?
  const existing = Math.max(0, Number.isFinite(availableForWithdrawal) ? availableForWithdrawal : 0);
  const afterProcessingPaisa = toPaisa(existing) + toPaisa(quote.netAmount);
  const belowMin = !zero && afterProcessingPaisa < toPaisa(MIN_WITHDRAWAL);
  const shortfall = belowMin
    ? paisaToTaka(Math.max(0, toPaisa(minGrossToReach(MIN_WITHDRAWAL - existing)) - toPaisa(quote.grossAmount)))
    : 0;

  const confirm = () => {
    if (zero || inFlight.current || mutation.isPending) return;
    inFlight.current = true;
    const key = keys.current.keyFor(String(toPaisa(quote.grossAmount)));
    mutation.mutate(key, {
      onSettled: () => {
        inFlight.current = false;
      },
    });
  };

  if (result) {
    return (
      <div className="stack" data-testid="process-success">
        <h3>{t('processWithdrawal.successTitle')}</h3>
        <p role="status">
          {t('processWithdrawal.successBody', {
            net: formatBDT(result.netAmount, locale),
            fee: formatBDT(result.feeAmount, locale),
          })}
        </p>
        <Button onClick={onClose} data-testid="process-done">
          {t('processWithdrawal.done')}
        </Button>
      </div>
    );
  }

  return (
    <div className="stack" data-testid="process-modal">
      <p>{t('processWithdrawal.intro')}</p>
      <dl className="kv" data-testid="process-quote">
        <dt>{t('processWithdrawal.gross')}</dt>
        <dd data-testid="process-gross">
          <AmountDisplay amount={quote.grossAmount} />
        </dd>
        <dt>{t('processWithdrawal.fee', { rate })}</dt>
        <dd data-testid="process-fee">
          <AmountDisplay amount={quote.feeAmount} negative />
        </dd>
        <dt>{t('processWithdrawal.net', { rate: 100 - rate })}</dt>
        <dd data-testid="process-net">
          <AmountDisplay amount={quote.netAmount} bold />
        </dd>
      </dl>
      {belowMin && (
        <p role="note" aria-live="polite" className="muted" data-testid="process-min-notice">
          {t('processWithdrawal.belowMin', {
            after: formatBDT(paisaToTaka(afterProcessingPaisa), locale),
            min: formatBDT(MIN_WITHDRAWAL, locale),
            more: formatBDT(shortfall, locale),
          })}
        </p>
      )}
      <p className="text-danger" data-testid="process-final">
        <strong>{t('processWithdrawal.final', { rate })}</strong>
      </p>
      {mutation.error && (
        <p className="text-danger" role="alert" data-testid="process-error">
          {processErrorText(mutation.error, t)}
        </p>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button variant="secondary" onClick={onClose} disabled={mutation.isPending} data-testid="process-cancel">
          {t('processWithdrawal.cancel')}
        </Button>
        <Button onClick={confirm} loading={mutation.isPending} disabled={zero} data-testid="process-confirm">
          {t('processWithdrawal.confirm')}
        </Button>
      </div>
    </div>
  );
}
